// Types + fetch for the admin Failure config screen (#262, ADR 0064) —
// absorbed from load-generator-ui's "Failure config" tab. Same
// `/failure-config` contract (ADR 0027, #149), now behind the BFF at
// `/api/admin/failure-config` with cookie auth instead of a shared secret.

export const FAILURE_MODES = ["exception", "timeout", "partial"] as const;
export type FailureMode = (typeof FAILURE_MODES)[number];

export const FAILURE_CONFIG_SERVICES = ["payment", "inventory", "shipping", "fraud"] as const;
export type FailureConfigService = (typeof FAILURE_CONFIG_SERVICES)[number];

export interface FailureConfig {
  payment: number;
  inventory: number;
  shipping: number;
  fraud: number;
  mode: FailureMode;
}

/** The Terraform seed defaults (`var.failure_rates` / `var.failure_mode`). */
export const DEFAULT_FAILURE_CONFIG: FailureConfig = {
  payment: 0.2,
  inventory: 0.2,
  shipping: 0.2,
  fraud: 0.2,
  mode: "exception",
};

export type FailureConfigResponse =
  | { kind: "config"; body: FailureConfig }
  | { kind: "unauthorized" }
  | { kind: "unavailable"; status: number; error: string };

export async function fetchFailureConfig(): Promise<FailureConfigResponse> {
  let res: Response;
  try {
    res = await fetch("/api/admin/failure-config", { credentials: "include" });
  } catch (err) {
    return { kind: "unavailable", status: 0, error: err instanceof Error ? err.message : "request failed (network)" };
  }
  if (res.status === 401) {
    return { kind: "unauthorized" };
  }
  if (res.status === 200) {
    return { kind: "config", body: (await res.json()) as FailureConfig };
  }
  return { kind: "unavailable", status: res.status, error: await errorMessage(res) };
}

export type PutFailureConfigResponse =
  | { kind: "ok"; body: FailureConfig }
  | { kind: "unauthorized" }
  | { kind: "error"; status: number; error: string };

export async function putFailureConfig(config: FailureConfig): Promise<PutFailureConfigResponse> {
  let res: Response;
  try {
    res = await fetch("/api/admin/failure-config", {
      method: "PUT",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(config),
    });
  } catch (err) {
    return { kind: "error", status: 0, error: err instanceof Error ? err.message : "request failed (network)" };
  }
  if (res.status === 401) {
    return { kind: "unauthorized" };
  }
  if (res.status === 200) {
    return { kind: "ok", body: (await res.json()) as FailureConfig };
  }
  return { kind: "error", status: res.status, error: await errorMessage(res) };
}

async function errorMessage(res: Response): Promise<string> {
  let error = `${res.status} ${res.statusText}`.trim();
  try {
    const b = (await res.json()) as { error?: string };
    if (b?.error) error = b.error;
  } catch {
    /* keep the status line */
  }
  return error;
}
